import { defineStore } from 'pinia';
import { usePreferredDark } from '@vueuse/core';
import { useCookies } from '@vueuse/integrations/useCookies';

export type Theme = 'light' | 'dark';

const COOKIE_NAME = 'pokor_theme';

interface ThemeState {
    theme: Theme;
}

export const useThemeStore = defineStore('theme', {
    state: (): ThemeState => ({
        theme: 'light',
    }),

    getters: {
        isDark: (state): boolean => state.theme === 'dark',
    },

    actions: {
        init(): void {
            const cookies = useCookies([COOKIE_NAME]);
            const stored = cookies.get<unknown>(COOKIE_NAME);
            if (stored === 'light' || stored === 'dark') {
                this.theme = stored;
            } else if (usePreferredDark().value) {
                this.theme = 'dark';
            }
            this.apply();
        },

        toggle(): void {
            this.theme = this.theme === 'dark' ? 'light' : 'dark';
            this._saveCookie();
            this.apply();
        },

        setTheme(theme: Theme): void {
            if (theme !== 'light' && theme !== 'dark') {
                return;
            }
            this.theme = theme;
            this._saveCookie();
            this.apply();
        },

        apply(): void {
            document.documentElement.setAttribute('data-theme', this.theme);
        },

        _saveCookie(): void {
            const domain = window.location.hostname.replace(/^app\./, '');
            const cookies = useCookies([COOKIE_NAME]);
            cookies.set(COOKIE_NAME, this.theme, {
                path: '/',
                domain: `.${domain}`,
                maxAge: 365 * 24 * 60 * 60,
                sameSite: 'lax',
            });
        },
    },
});
